/**
 * Auth provider for the helpdesk widget flow.
 *
 * Receives the initial GAIA_AUTH payload from CopilotRoot (via `initialAuth` prop)
 * and listens for subsequent messages (token refreshes, page context updates).
 *
 * Auth is based solely on the Gorgias JWT (X-Gaia-Apps-Token) — no Google sign-in required.
 * When a Google session cookie exists, the Google identity is used for richer
 * user info (name, picture), but it is never required.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { configureChatApi } from '@/client/lib/chatApi';
import { setCustomHeaders } from '@/client/lib/api';
import { AuthContext, type AuthState, type AuthContextValue, type GoogleUser } from './auth-context';

interface AuthProviderProps {
  initialAuth: Record<string, unknown> | null;
  children: ReactNode;
}

/** Parse a GAIA_AUTH payload into helpdesk tokens and state. */
function parseWidgetAuth(data: Record<string, unknown>) {
  const appsToken = (data.appsToken as string) ?? '';
  const userName = (data.userName as string) ?? null;
  const userEmail = (data.userEmail as string) ?? null;
  const pageUrl = (data.pageUrl as string) ?? (document.referrer || '');
  const pageTitle = (data.pageTitle as string) ?? '';

  return { appsToken, userName, userEmail, pageUrl, pageTitle };
}

export function AuthProvider({ initialAuth, children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState | null>(null);
  const helpdeskTokensRef = useRef<{ appsToken: string; userName: string | null; userEmail: string | null }>({
    appsToken: '', userName: null, userEmail: null,
  });
  const [tokenVersion, setTokenVersion] = useState(0);
  const googleSessionChecked = useRef(false);

  /** Configure API layer for helpdesk widget (product token headers). */
  const configureHelpdeskAuth = useCallback((tokens: {
    appsToken: string;
    userName: string | null; userEmail: string | null;
  }) => {
    helpdeskTokensRef.current = tokens;
    const headersProvider = () => {
      const t = helpdeskTokensRef.current;
      const h: Record<string, string> = { 'X-Gaia-Apps-Token': t.appsToken };
      if (t.userName) h['X-Gaia-User-Name'] = t.userName;
      if (t.userEmail) h['X-Gaia-User-Email'] = t.userEmail;
      return h;
    };
    configureChatApi({ basePath: '/api/chat', headersProvider });
    setCustomHeaders(headersProvider());
  }, []);

  /** Request a fresh token from the parent page before each agent run. */
  const requestTokenRefresh = useCallback(() => {
    window.parent.postMessage({ type: 'GAIA_EMBED_REFRESH_TOKEN' }, '*');
  }, []);

  // Check Google session (non-blocking).
  // Enriches user info when a Google session cookie exists.
  const checkGoogleSession = useCallback((isInitialCheck = false) => {
    if (isInitialCheck) {
      setState((prev) => (prev ? { ...prev, googleSessionLoading: true } : prev));
    }
    fetch('/api/auth/me', { credentials: 'include' })
      .then(async (res) => {
        if (res.ok) {
          const data = (await res.json()) as { user: GoogleUser };
          setState((prev) => (prev ? { ...prev, googleUser: data.user, googleSessionLoading: false } : prev));
        } else {
          setState((prev) => (prev ? { ...prev, googleSessionLoading: false } : prev));
        }
      })
      .catch(() => {
        // Network error — proceed without Google session
        setState((prev) => (prev ? { ...prev, googleSessionLoading: false } : prev));
      });
  }, []);

  // Process initial auth payload on mount (captured by CopilotRoot)
  useEffect(() => {
    if (!initialAuth) return;
    const { appsToken, userName, userEmail, pageUrl, pageTitle } = parseWidgetAuth(initialAuth);
    if (!appsToken) return;

    configureHelpdeskAuth({ appsToken, userName, userEmail });
    setTokenVersion(v => v + 1);
    setState({
      pageUrl,
      pageTitle,
      userName,
      userEmail,
      googleUser: null,
      googleSessionLoading: true,
      authError: null,
      userRole: null,
    });
    // Only run on mount — initialAuth is captured once by CopilotRoot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for subsequent messages (token refreshes, page context updates)
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      const data = event.data;

      // Handle widget token refreshes (parent re-sends GAIA_AUTH)
      if (data?.type === 'GAIA_AUTH') {
        const { appsToken, userName, userEmail, pageUrl, pageTitle } = parseWidgetAuth(data);
        if (!appsToken) return;

        configureHelpdeskAuth({ appsToken, userName, userEmail });
        setTokenVersion(v => v + 1);
        // Preserve existing googleUser so the sign-in screen doesn't flash
        // when the parent re-sends GAIA_AUTH (e.g. on token refresh).
        setState((prev) => ({
          pageUrl,
          pageTitle,
          userName,
          userEmail,
          googleUser: prev?.googleUser ?? null,
          googleSessionLoading: prev?.googleSessionLoading ?? false,
          authError: null,
          userRole: prev?.userRole ?? null,
        }));
        // Re-check Google session if not already known (cookie may still be valid)
        if (!googleSessionChecked.current) {
          googleSessionChecked.current = true;
          checkGoogleSession();
        }
        return;
      }

      // Handle page context updates (SPA navigation in parent)
      if (data?.type === 'GAIA_PAGE_CONTEXT') {
        const { pageUrl, pageTitle } = data as { pageUrl?: string; pageTitle?: string };
        setState((prev) =>
          prev
            ? { ...prev, pageUrl: pageUrl ?? prev.pageUrl, pageTitle: pageTitle ?? prev.pageTitle }
            : prev,
        );
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [configureHelpdeskAuth, checkGoogleSession]);

  // Check for existing Google session once on initial auth
  useEffect(() => {
    if (state && !googleSessionChecked.current) {
      googleSessionChecked.current = true;
      checkGoogleSession(true);
    }
  }, [state, checkGoogleSession]);

  // Listen for Google auth success from the popup window.
  // The popup relays the Google access token via postMessage instead of
  // setting the cookie itself (which would land in the wrong CHIPS partition).
  // We validate the token from the iframe context so the server sets
  // the session cookie in the correct partition.
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type !== 'GAIA_EMBED_GOOGLE_AUTH_SUCCESS') return;

      if (event.data.accessToken) {
        // Same-origin popup: validate token and set cookie from iframe context
        fetch('/api/auth/google/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ access_token: event.data.accessToken }),
        })
          .then((res) => {
            if (res.ok) {
              checkGoogleSession();
            } else {
              setState((prev) =>
                prev ? { ...prev, authError: 'Google authentication failed.' } : prev,
              );
            }
          })
          .catch(() => {
            setState((prev) =>
              prev ? { ...prev, authError: 'Google authentication failed.' } : prev,
            );
          });
      } else {
        // Cross-origin callback: cookie already set by the server, just refresh session
        checkGoogleSession();
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [checkGoogleSession]);

  /** Open the Google sign-in popup.
   *  On PR preview subdomains, open on the bare preview domain so the origin
   *  matches the one registered in Google OAuth. */
  /** Open the Google sign-in popup.
   *  On PR preview subdomains, open on the bare preview domain so the origin
   *  matches the one registered in Google OAuth (no wildcard for PR subdomains). */
  const signInWithGoogle = useCallback(() => {
    const PREVIEW_DOMAIN = 'preview.gaia.gorgias-decision-engine.com';
    const host = window.location.hostname;
    const isPreviewSubdomain = host.endsWith(`.${PREVIEW_DOMAIN}`) && host !== PREVIEW_DOMAIN;
    const authUrl = isPreviewSubdomain
      ? `https://${PREVIEW_DOMAIN}/embed/auth`
      : '/embed/auth';
    const url = new URL(authUrl, window.location.origin);
    url.searchParams.set('opener_origin', window.location.origin);
    window.open(url.toString(), '_blank', 'popup,width=500,height=600');
  }, []);

  /** Sign out from Google session — clears cookie and resets googleUser. */
  const signOutFromGoogle = useCallback(() => {
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
      .catch(() => {});
    // Don't reset googleSessionChecked — otherwise the useEffect immediately
    // re-checks the session, racing the logout POST and restoring googleUser.
    setState((prev) => (prev ? { ...prev, googleUser: null } : prev));
  }, []);

  // Update headers when tokens refresh
  useEffect(() => {
    if (!state || tokenVersion === 0) return;

    const t = helpdeskTokensRef.current;
    const headers: Record<string, string> = { 'X-Gaia-Apps-Token': t.appsToken };
    if (t.userName) headers['X-Gaia-User-Name'] = t.userName;
    if (t.userEmail) headers['X-Gaia-User-Email'] = t.userEmail;

    configureChatApi({
      basePath: '/api/chat',
      headersProvider: () => headers,
    });
    setCustomHeaders(headers);
  }, [state, tokenVersion]);

  const getAppsToken = useCallback(() => helpdeskTokensRef.current.appsToken, []);

  if (!state) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
        <p>Connecting...</p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
        >
          Reload
        </button>
      </div>
    );
  }

  const contextValue: AuthContextValue = {
    ...state,
    requestTokenRefresh,
    signInWithGoogle,
    signOutFromGoogle,
    getAppsToken,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
